import { ReactElement, useRef, useEffect } from 'react';
import { Box, Card, CardContent, Typography, useMediaQuery, useTheme, LinearProgress } from '@mui/material';
import { useBrand } from '../../context/BrandContext';
import { SkeletonLoader } from './SkeletonLoader';
import { useCountAnimation } from '../../hooks/useCountAnimation';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

interface StatCardProps {
  icon: string;
  value: number;
  label: string;
  loading?: boolean;
  animationDelay?: number;
  showProgressBar?: boolean;
  maxValue?: number;
}

const StatCard = ({ icon, value, label, loading = false, animationDelay = 0, showProgressBar = false, maxValue = 100 }: StatCardProps): ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { brand } = useBrand();
  const cardRef = useRef<HTMLDivElement>(null!);
  const hasAnimated = useRef(false);
  const isVisible = useIntersectionObserver(cardRef, { threshold: 0.3 });
  const readyToAnimate = isVisible && !loading;

  useEffect(() => {
    if (readyToAnimate) hasAnimated.current = true;
  }, [readyToAnimate]);

  const shouldAnimate = readyToAnimate || hasAnimated.current;
  const animatedValue = useCountAnimation({ end: value, duration: 1500, shouldAnimate, delay: animationDelay });
  const progressValue = (value / maxValue) * 100;
  const animatedProgressValue = useCountAnimation({ end: progressValue, duration: 1500, shouldAnimate, delay: animationDelay });

  return (
    <Card ref={cardRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <SkeletonLoader count={2} />
        ) : (
          <Box sx={{ textAlign: 'center', py: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', flexGrow: 1 }}>
            <Box>
              <i className={icon} style={{ fontSize: isMobile ? 42 : 48, color: brand.primaryColor, marginBottom: 12 }} />
              <Typography
                variant={isMobile ? "h5" : "h4"} 
                sx={{ fontWeight: 700, fontFamily: 'Helvetica, Arial, sans-serif !important', color: '#212529', mt: 0.5, mb: 0.5 }}
              >
                {animatedValue.toLocaleString()}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                {label}
              </Typography>
            </Box>
            
            {showProgressBar && (
              <Box sx={{ mt: 3, px: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Progression
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: brand.primaryColor }}>
                    {Math.min(value, maxValue)}/{maxValue}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(animatedProgressValue, 100)}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    '& .MuiLinearProgress-bar': { borderRadius: 5, backgroundColor: brand.primaryColor }
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StatCard;
